/**
 * PaywallProvider — global error consumer pro:
 * - PaywallModal (403 entitlement / 422 plan_count_limit_reached)
 * - TenantStatusBanner (503 tenant_suspended / tenant_deleted)
 * - PlatformAddressNotice (404 platform_address_hidden /
 *   401 platform_address_login_required — KAN-675)
 *
 * Wireuje se kolem celé app v main.tsx (uvnitř QueryClientProvider).
 * Subscribe na MutationCache + QueryCache; při ``ApiError`` matching
 * paywall nebo tenant-status shape vyvolá příslušnou UI komponentu.
 *
 * PR-23 (paywall) + PR-30 (tenant status banner).
 */

import { useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  PaywallModal,
  paywallFromError,
  type PaywallContext,
} from "@/components/PaywallModal";
import {
  PlatformAddressNotice,
  platformAddressFromError,
  type PlatformAddressRefusal,
} from "@/components/PlatformAddressNotice";
import {
  TenantStatusBanner,
  tenantStatusFromError,
  type TenantStatusContext,
} from "@/components/TenantStatusBanner";
import { ApiError } from "@/lib/api";

export function PaywallProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const qc = useQueryClient();
  const [paywall, setPaywall] = useState<PaywallContext | null>(null);
  const [tenantStatus, setTenantStatus] =
    useState<TenantStatusContext | null>(null);
  const [addressRefusal, setAddressRefusal] =
    useState<PlatformAddressRefusal | null>(null);

  useEffect(() => {
    function handleError(
      err: unknown,
      meta: Record<string, unknown> | undefined,
      kind: "query" | "mutation",
    ) {
      if (!(err instanceof ApiError)) return;
      // Tenant suspension takes precedence — full overlay, žádný paywall
      // modal vedle.
      const statusCtx = tenantStatusFromError(err);
      if (statusCtx) {
        setTenantStatus(statusCtx);
        return;
      }
      // A closed assigned address refuses the whole customer surface,
      // so it outranks the paywall too: offering to sell a plan on a
      // page that will not load either way is noise.
      const addressCtx = platformAddressFromError(err);
      if (addressCtx) {
        setAddressRefusal(addressCtx);
        return;
      }
      const paywallCtx = paywallFromError(err);
      if (!paywallCtx) return;
      // Auto-fetch (mount-time probe in shared chrome / dashboard
      // widget / onboarding step) of an entitlement-gated endpoint:
      // suppress the modal. The hook author marked the query with
      // ``meta.entitlement`` via :func:`useGatedQuery`, the
      // preflight short-circuit normally prevents the fetch entirely,
      // and the only way we land here is a race / per-tenant override
      // drift / plan-fetch error. In all three cases a global modal
      // is surprising — the component renders its own paywall banner
      // (it has the data it needs from useAdminPlan). Mutations are
      // user-initiated so they still pop the modal regardless of
      // meta-tagging.
      if (
        kind === "query" &&
        paywallCtx.kind === "entitlement" &&
        typeof meta?.entitlement === "string"
      ) {
        return;
      }
      setPaywall(paywallCtx);
    }

    // MutationCache → POST/PATCH/DELETE chyby (= většina paywall případů,
    // vždy user-initiated).
    const unsubMutations = qc.getMutationCache().subscribe((event) => {
      if (event.type === "updated" && event.mutation.state.status === "error") {
        handleError(
          event.mutation.state.error,
          event.mutation.options.meta,
          "mutation",
        );
      }
    });

    // QueryCache → GET chyby (méně časté pro paywall, ale např. /admin/plans
    // může vrátit 403 pokud platform admin přijde o membership role; pro
    // tenant-status banner naopak většina hitů přijde právě z GET queries
    // co volají fetch při admin shell mountu).
    const unsubQueries = qc.getQueryCache().subscribe((event) => {
      if (event.type === "updated" && event.query.state.status === "error") {
        handleError(event.query.state.error, event.query.meta, "query");
      }
    });

    return () => {
      unsubMutations();
      unsubQueries();
    };
  }, [qc]);

  return (
    <>
      {/* The refusal REPLACES the app rather than covering it. Every
          request behind it came back refused, so what it would be
          covering is a broken page — and an overlay the visitor can
          dismiss would hand them exactly that. Suspension keeps its
          overlay: a suspended tenant's own admin still has working
          screens underneath. */}
      {addressRefusal ? (
        <PlatformAddressNotice refusal={addressRefusal} />
      ) : (
        children
      )}
      {paywall && (
        <PaywallModal
          context={paywall}
          onClose={() => setPaywall(null)}
        />
      )}
      {tenantStatus && <TenantStatusBanner context={tenantStatus} />}
    </>
  );
}
